/** Voice in and out (ADR-0070): the Assistant microphone, Channels › Voice, Models › voice choices. */
export default {
  en: {
    lang: { ar: "Arabic", en: "English" },
    where: { local: "on our servers", online: "online" },
    sample: {
      ar: "مرحباً، هذا صوتي. سأقرأ لك الإجابات بهذه الطريقة.",
      en: "Hello, this is my voice. I will read your answers like this.",
    },
    assistant: {
      mic: "Talk to the agent: hold to talk, or tap to start and stop listening",
      micHint: "Hold to talk, or tap once to talk hands-free",
      micStop: "Stop listening",
      off: "Voice is not turned on for this agent",
      listening: "Listening… let go when you finish",
      listeningHandsFree: "Listening… speak, then pause. Tap the microphone to stop.",
      hearing: "Hearing you…",
      readAloud: "Read answers aloud",
      stop: "Stop speaking",
      private: "Private conversation: your voice stays on our own servers.",
      spokenLocally: "Spoken on our own servers",
    },
    problem: {
      denied: "Your browser blocked the microphone. Allow it in the address bar, then try again.",
      unsupported: "This browser cannot record sound. Try a recent Chrome, Edge, Firefox or Safari.",
      failed: "The microphone could not be opened. Check that one is connected, then try again.",
      asr_unavailable: "I couldn't make out what was said just now. Please try again, or type your question.",
      tts_unavailable: "The answer couldn't be read aloud just now. It is shown above.",
      private_unavailable: "This agent's conversations are private, and the speech service on our own servers isn't running. Type your question, or ask an operator to switch on local voice.",
      voice_off: "Voice is not turned on for this agent.",
      unauthorized: "Your session has ended. Sign in again to use voice.",
      not_allowed: "Voice isn't allowed for this agent or for your account.",
      unsupported_sample_rate: "The sound format wasn't accepted. Reload the page and try again.",
      connection: "Voice couldn't connect. Check your connection and try again.",
    },
    widget: {
      label: "Voice (microphone button)",
      help: "Visitors can talk to the agent and hear the answers. Voice must also be set up for the agent in the Voice tab.",
      on: "Voice on",
    },
    listen: {
      label: "Listen ({{language}})",
      aria: "Listen to a sample in {{language}}",
      failed: "The sample couldn't be played. Check the model's connection.",
      column: "Listen",
    },
    choices: {
      title: "Voice",
      intro: "Which models listen to people and which speak the answers, with their backups. Private conversations only use models on our own servers.",
      main: "Main model",
      mainHelp: "Used for every conversation that is not private.",
      backups: "Backups, in order",
      backupsHelp: "Tried one after another when the main model fails. Only models ready for this stage can be chosen.",
      order: "Backup order",
      private: "Private conversations use",
      privateHelp: "Models on our own servers. They take no other traffic.",
      noLocal: "No model on our own servers can do this yet.",
      noPrivate: "Private voice conversations will be refused",
      save: "Save voice choices",
      saved: "Voice choices saved",
      listen: {
        title: "Listening (speech to text)",
        help: "Turns what people say into text for the agent.",
        none: "No model that understands speech is added yet. Add one under Models.",
      },
      speak: {
        title: "Speaking (text to speech)",
        help: "Reads the agent's answers aloud. Each voice speaks some languages.",
        none: "No model that speaks is added yet. Add one under Models.",
      },
    },
    setup: {
      intro: "Let people talk to an agent and hear its answers, in the Assistant and the website chat.",
      where: "Which models listen and speak is chosen in Models › Speech. Here you choose what each agent does with voice.",
      add: "Set up voice for an agent",
      addTitle: "Set up voice",
      editTitle: "Voice for {{name}}",
      none: "No agent has voice yet.",
      agent: "Agent",
      agentHelp: "The agent people will talk to.",
      agentPlaceholder: "Choose an agent",
      alreadySet: "already set up",
      languages: "Languages",
      state: "Voice",
      on: "On",
      off: "Off",
      privacy: "Privacy",
      privateTag: "Private",
      normalTag: "Normal",
      enabled: "Turn on voice",
      enabledHelp: "Shows the microphone for this agent in the Assistant and, if turned on there, in the website chat.",
      voiceFor: "Voice for {{language}}",
      voiceHelp: "Leave empty to use the model's usual voice for this language.",
      voiceAuto: "Usual voice",
      speed: "Speaking speed",
      slower: "Slower",
      normal: "Normal",
      faster: "Faster",
      readAloud: "Read answers aloud",
      readAloudHelp: "People can still turn this off for themselves in the Assistant.",
      summary: "Short spoken version",
      summaryHelp: "Answers with tables or long text are not read in full. The agent can give its own short version; if it doesn't:",
      summaryFirst: "Read the first",
      sentences: "sentences",
      summaryModel: "Have the chat model write one or two spoken sentences",
      private: "Private conversations",
      privateHelp: "Listen and speak only with models on our own servers. Nothing said leaves the organisation.",
      hints: "Words to recognise",
      hintsHelp: "Names and places people often say, separated by commas. Helps the listening model spell them right.",
      hintsPlaceholder: "for example: padel, Playtomic, Al Quoz",
      quietNote: "Quiet hours and consent (Settings tab) apply to messages the agent starts. Spoken replies only answer people who are talking.",
      save: "Save",
      cancel: "Cancel",
      saved: "Voice saved for {{name}}",
      edit: "Edit",
      editAria: "Edit voice for {{name}}",
      remove: "Remove",
      removeAria: "Remove voice for {{name}}",
      removeTitle: "Remove voice for {{name}}? The microphone disappears for this agent.",
      removed: "Voice removed for {{name}}",
      sep: ", ",
    },
    use: {
      title: "What will be used",
      whyNormal: "Conversations use the main models chosen in Models › Speech, with their backups.",
      whyPrivate: "Private conversations never leave our servers: only models on our own servers listen and speak.",
      listen: "Listening",
      speak: "Speaking",
      backup: "then",
      noModel: "No model can do this right now. Ask a model administrator to choose one in Models › Speech.",
      noPrivate: "No speech model can take private conversations right now. Ask an operator to switch on local voice (Health and usage explains how).",
      unknown: "Couldn't load the speech models right now.",
      changeIn: "To change these, use Models › Speech.",
      open: "Open Models › Speech",
    },
    issues: {
      agent_required: "Choose an agent.",
      languages_required: "Choose at least one language.",
      hints_too_long: "Keep the words to recognise under 200 characters.",
    },
  },
  ar: {
    lang: { ar: "العربية", en: "الإنجليزية" },
    where: { local: "على خوادمنا", online: "عبر الإنترنت" },
    sample: {
      ar: "مرحباً، هذا صوتي. سأقرأ لك الإجابات بهذه الطريقة.",
      en: "Hello, this is my voice. I will read your answers like this.",
    },
    assistant: {
      mic: "تحدّث مع الوكيل: اضغط مطوّلاً للتحدث، أو انقر لبدء الاستماع وإيقافه",
      micHint: "اضغط مطوّلاً للتحدث، أو انقر مرة للتحدث دون استخدام اليدين",
      micStop: "إيقاف الاستماع",
      off: "الصوت غير مفعّل لهذا الوكيل",
      listening: "أستمع… اترك الزر عند الانتهاء",
      listeningHandsFree: "أستمع… تحدّث ثم توقف قليلاً. انقر الميكروفون للإيقاف.",
      hearing: "أسمعك…",
      readAloud: "قراءة الإجابات بصوت عالٍ",
      stop: "إيقاف القراءة",
      private: "محادثة خاصة: صوتك يبقى على خوادمنا.",
      spokenLocally: "يُنطق على خوادمنا",
    },
    problem: {
      denied: "منع المتصفح استخدام الميكروفون. اسمح به من شريط العنوان ثم حاول مجدداً.",
      unsupported: "هذا المتصفح لا يستطيع تسجيل الصوت. جرّب إصداراً حديثاً من Chrome أو Edge أو Firefox أو Safari.",
      failed: "تعذّر فتح الميكروفون. تأكد من توصيله ثم حاول مجدداً.",
      asr_unavailable: "لم أتمكن من فهم ما قيل الآن. حاول مجدداً أو اكتب سؤالك.",
      tts_unavailable: "تعذّرت قراءة الإجابة بصوت عالٍ الآن. وهي معروضة أعلاه.",
      private_unavailable: "محادثات هذا الوكيل خاصة، وخدمة الكلام على خوادمنا لا تعمل. اكتب سؤالك، أو اطلب من أحد المشغّلين تشغيل الصوت المحلي.",
      voice_off: "الصوت غير مفعّل لهذا الوكيل.",
      unauthorized: "انتهت جلستك. سجّل الدخول مجدداً لاستخدام الصوت.",
      not_allowed: "الصوت غير مسموح لهذا الوكيل أو لحسابك.",
      unsupported_sample_rate: "لم تُقبل صيغة الصوت. أعد تحميل الصفحة وحاول مجدداً.",
      connection: "تعذّر اتصال الصوت. تحقق من اتصالك وحاول مجدداً.",
    },
    widget: {
      label: "الصوت (زر الميكروفون)",
      help: "يستطيع الزوار التحدث مع الوكيل وسماع الإجابات. يجب أيضاً إعداد الصوت للوكيل في تبويب الصوت.",
      on: "الصوت مفعّل",
    },
    listen: {
      label: "استمع ({{language}})",
      aria: "استمع إلى عيّنة باللغة {{language}}",
      failed: "تعذّر تشغيل العيّنة. تحقق من اتصال النموذج.",
      column: "استمع",
    },
    choices: {
      title: "الصوت",
      intro: "أي النماذج تستمع إلى الناس وأيها تنطق الإجابات، مع النماذج الاحتياطية. المحادثات الخاصة تستخدم فقط النماذج على خوادمنا.",
      main: "النموذج الرئيسي",
      mainHelp: "يُستخدم لكل محادثة غير خاصة.",
      backups: "النماذج الاحتياطية بالترتيب",
      backupsHelp: "تُجرَّب واحداً تلو الآخر عند تعطل النموذج الرئيسي. يمكن اختيار النماذج الجاهزة لهذه المرحلة فقط.",
      order: "ترتيب النماذج الاحتياطية",
      private: "المحادثات الخاصة تستخدم",
      privateHelp: "نماذج على خوادمنا لا تستقبل أي طلبات أخرى.",
      noLocal: "لا يوجد نموذج على خوادمنا يستطيع ذلك بعد.",
      noPrivate: "ستُرفض المحادثات الصوتية الخاصة",
      save: "حفظ اختيارات الصوت",
      saved: "تم حفظ اختيارات الصوت",
      listen: {
        title: "الاستماع (من كلام إلى نص)",
        help: "يحوّل ما يقوله الناس إلى نص للوكيل.",
        none: "لم يُضف بعد نموذج يفهم الكلام. أضف واحداً من النماذج.",
      },
      speak: {
        title: "التحدث (من نص إلى كلام)",
        help: "يقرأ إجابات الوكيل بصوت عالٍ. كل صوت يتحدث لغات معيّنة.",
        none: "لم يُضف بعد نموذج يتكلّم. أضف واحداً من النماذج.",
      },
    },
    setup: {
      intro: "اسمح للناس بالتحدث مع وكيل وسماع إجاباته، في المساعد ومحادثة الموقع.",
      where: "تُختار النماذج التي تستمع وتتحدث من النماذج › الكلام. هنا تحدد ما يفعله كل وكيل بالصوت.",
      add: "إعداد الصوت لوكيل",
      addTitle: "إعداد الصوت",
      editTitle: "الصوت لـ {{name}}",
      none: "لا يوجد وكيل مفعّل له الصوت بعد.",
      agent: "الوكيل",
      agentHelp: "الوكيل الذي سيتحدث معه الناس.",
      agentPlaceholder: "اختر وكيلاً",
      alreadySet: "معدّ مسبقاً",
      languages: "اللغات",
      state: "الصوت",
      on: "مفعّل",
      off: "متوقف",
      privacy: "الخصوصية",
      privateTag: "خاصة",
      normalTag: "عادية",
      enabled: "تفعيل الصوت",
      enabledHelp: "يُظهر الميكروفون لهذا الوكيل في المساعد، وفي محادثة الموقع إذا فُعّل هناك.",
      voiceFor: "الصوت للغة {{language}}",
      voiceHelp: "اتركه فارغاً لاستخدام الصوت المعتاد للنموذج في هذه اللغة.",
      voiceAuto: "الصوت المعتاد",
      speed: "سرعة الكلام",
      slower: "أبطأ",
      normal: "عادية",
      faster: "أسرع",
      readAloud: "قراءة الإجابات بصوت عالٍ",
      readAloudHelp: "يمكن للناس إيقاف ذلك لأنفسهم في المساعد.",
      summary: "نسخة منطوقة قصيرة",
      summaryHelp: "الإجابات التي فيها جداول أو نص طويل لا تُقرأ كاملة. يمكن للوكيل أن يقدّم نسخته القصيرة؛ وإن لم يفعل:",
      summaryFirst: "اقرأ أول",
      sentences: "جمل",
      summaryModel: "اطلب من نموذج المحادثة كتابة جملة أو جملتين منطوقتين",
      private: "المحادثات الخاصة",
      privateHelp: "الاستماع والتحدث فقط بنماذج على خوادمنا. لا يخرج أي كلام من المؤسسة.",
      hints: "كلمات يجب التعرف عليها",
      hintsHelp: "أسماء وأماكن يذكرها الناس كثيراً، تفصل بينها فواصل. تساعد نموذج الاستماع على كتابتها بشكل صحيح.",
      hintsPlaceholder: "مثلاً: بادل، Playtomic، القوز",
      quietNote: "تنطبق ساعات الهدوء والموافقات (تبويب الإعدادات) على الرسائل التي يبدؤها الوكيل. الردود المنطوقة تجيب فقط من يتحدث.",
      save: "حفظ",
      cancel: "إلغاء",
      saved: "تم حفظ الصوت لـ {{name}}",
      edit: "تعديل",
      editAria: "تعديل الصوت لـ {{name}}",
      remove: "إزالة",
      removeAria: "إزالة الصوت لـ {{name}}",
      removeTitle: "إزالة الصوت لـ {{name}}؟ سيختفي الميكروفون لهذا الوكيل.",
      removed: "تمت إزالة الصوت لـ {{name}}",
      sep: "، ",
    },
    use: {
      title: "ما الذي سيُستخدم",
      whyNormal: "تستخدم المحادثات النماذج الرئيسية المختارة في النماذج › الكلام، مع النماذج الاحتياطية.",
      whyPrivate: "المحادثات الخاصة لا تغادر خوادمنا أبداً: فقط النماذج على خوادمنا تستمع وتتحدث.",
      listen: "الاستماع",
      speak: "التحدث",
      backup: "ثم",
      noModel: "لا يوجد نموذج يستطيع ذلك الآن. اطلب من مسؤول النماذج اختيار نموذج في النماذج › الكلام.",
      noPrivate: "لا يوجد نموذج كلام يستطيع تولّي المحادثات الخاصة الآن. اطلب من أحد المشغّلين تشغيل الصوت المحلي (تشرح صفحة الصحة والاستخدام الطريقة).",
      unknown: "تعذّر تحميل نماذج الكلام الآن.",
      changeIn: "لتغييرها، استخدم النماذج › الكلام.",
      open: "افتح النماذج › الكلام",
    },
    issues: {
      agent_required: "اختر وكيلاً.",
      languages_required: "اختر لغة واحدة على الأقل.",
      hints_too_long: "اجعل الكلمات أقل من 200 حرف.",
    },
  },
};
